/**
 * useHotkeys — declarative keyboard shortcuts.
 *
 * Pattern matches Linear / GitHub style: `g t` then `t` to jump
 * to Tasks within 1.5 s. Single-key shortcuts (e.g. `/` to focus
 * the search bar) work too.
 *
 * Shortcut shape:
 *   - Sequences:    `'g t'`       — press `g`, release, then `t`
 *   - Single keys:  `'/'`         — focus search input
 *   - Modifiers:    `'mod+k'`     — Ctrl+K on Linux/Win, Cmd+K on Mac
 *   - Escape:       `'Escape'`    — close any open modal/menu
 *
 * The hook returns nothing. Components subscribe via the
 * `hotkey` map in `hotkey-registry.ts` and use `useNavigate` from
 * react-router internally.
 *
 * Tips for consumers:
 *   - Use `useHotkeys()` at the AppShell root so the listener is
 *     active across all screens.
 *   - Gate shortcuts on a `predicate` (e.g. `enabled: !modalOpen`)
 *     so they don't fire while the operator is typing in an input.
 */

import { useEffect } from 'react';

export type HotkeyHandler = (e: KeyboardEvent) => void;

export interface HotkeySpec {
  /** Sequence like `'g t'` or single key like `'/'`. */
  combo: string;
  /** Description for the ?-help dialog. */
  description: string;
  /** Optional predicate; if returns false, the hotkey is skipped. */
  enabled?: () => boolean;
  /** Optional element ref whose focus/visibility must be checked. */
  inEditableContext?: () => boolean;
}

export const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function eventInEditableTarget(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function normalizeCombo(combo: string): string {
  return combo
    .toLowerCase()
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');
}

/** Parse a single key token like `'mod+k'`, `'escape'`, `'?'`. */
function matchKeyToken(token: string, e: KeyboardEvent): boolean {
  const parts = token.split('+').map((p) => p.trim());
  const mods = new Set(parts.slice(0, -1));
  const key = parts[parts.length - 1]!;
  const wantMod = mods.has('mod') || mods.has('ctrl') || mods.has('cmd');
  const wantShift = mods.has('shift');
  const wantAlt = mods.has('alt') || mods.has('option');
  const hasMod = isMac ? e.metaKey : e.ctrlKey;
  const hasShift = e.shiftKey;
  const hasAlt = e.altKey;
  if (wantMod && !hasMod) return false;
  if (wantShift && !hasShift) return false;
  if (wantAlt && !hasAlt) return false;
  if (!wantMod && hasMod) return false;
  if (!wantShift && hasShift) return false;
  if (!wantAlt && hasAlt) return false;
  // Escape / Tab / Enter are matched by key name; everything else
  // by e.key.toLowerCase().
  const k = e.key.toLowerCase();
  if (key === 'escape') return e.key === 'Escape';
  if (key === 'enter') return e.key === 'Enter';
  if (key === 'tab') return e.key === 'Tab';
  if (key === 'arrowup') return e.key === 'ArrowUp';
  if (key === 'arrowdown') return e.key === 'ArrowDown';
  if (key === 'arrowleft') return e.key === 'ArrowLeft';
  if (key === 'arrowright') return e.key === 'ArrowRight';
  if (key === 'space') return e.key === ' ';
  if (key === '?' || key === '/') return k === key;
  return k === key;
}

/**
 * Hook. Pass an array of `[combo, handler, predicate?]` tuples.
 * Returns nothing. Sequences are matched across two key events:
 * the first key in the sequence is recorded, and if the next key
 * within `seqWindowMs` matches the second, the handler fires.
 */
export function useHotkeys(
  bindings: Array<[string, HotkeyHandler, (() => boolean)?]>,
  options: { seqWindowMs?: number; skipInEditable?: boolean } = {},
): void {
  const seqWindowMs = options.seqWindowMs ?? 1500;
  const skipInEditable = options.skipInEditable ?? true;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let firstKey: string | null = null;
    let firstKeyAt = 0;
    let resetTimer: number | null = null;

    function clearFirst() {
      firstKey = null;
      firstKeyAt = 0;
      if (resetTimer !== null) {
        window.clearTimeout(resetTimer);
        resetTimer = null;
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      // Skip if the operator is typing into an input.
      if (skipInEditable && eventInEditableTarget(e)) {
        // ... except Escape and mod shortcuts, which we keep active.
        if (e.key !== 'Escape' && !e.metaKey && !e.ctrlKey) return;
      }
      for (const [combo, handler, predicate] of bindings) {
        if (predicate && !predicate()) continue;
        const norm = normalizeCombo(combo);
        const tokens = norm.split(' ');
        if (tokens.length === 1) {
          if (matchKeyToken(tokens[0]!, e)) {
            handler(e);
            clearFirst();
            return;
          }
        } else if (tokens.length === 2) {
          const [a, b] = tokens as [string, string];
          const now = Date.now();
          // If we have a stale first key, drop it.
          if (firstKey && now - firstKeyAt > seqWindowMs) {
            clearFirst();
          }
          if (!firstKey) {
            // Record the first key as the start of a sequence.
            if (matchKeyToken(a, e)) {
              firstKey = a;
              firstKeyAt = now;
              if (resetTimer !== null) window.clearTimeout(resetTimer);
              resetTimer = window.setTimeout(clearFirst, seqWindowMs);
              return;
            }
          } else if (firstKey === a) {
            if (matchKeyToken(b, e)) {
              handler(e);
              clearFirst();
              return;
            }
          }
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearFirst();
    };
  }, [bindings, seqWindowMs, skipInEditable]);
}
